import { Box } from '@mui/material'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import { editObject, getObject, addObject } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import useNavigate from '../../hooks/useNavigate'
import Form from '../../form/Form'
import { useOutletContext } from 'react-router-dom'
import Toolbar from '../../toolbar/Toolbar'
import { getOutcomeFields } from './AccountingFields'
import dayjs from 'dayjs'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import useSaveEntity from '../../hooks/useSaveEntity'

const ROUTES = {
  EDIT: '/outcomes/edit',
  VIEW: '/outcomes/view',
  ADD: '/outcomes/add'
}

const getNextInvoiceDate = (data) => {
  if (!data.is_recurent || !data.recure_every || !data.date) return null
  const date = dayjs(data.date).add(
    data.recure_every === 1 ? 1 : data.recure_every === 2 ? 1 : data.recure_every === 3 ? 3 : 1,
    data.recure_every === 4 ? 'year' : data.recure_every === 1 ? 'week' : 'month'
  )
  return date.format('YYYY-MM-DD')
}

const buildPayload = ({ data, user, mode }) => {
  const common = {
    periodYear: dayjs(data.date).format('YYYY'),
    periodMonth: dayjs(data.date).format('MM'),
    periodYearMonth: dayjs(data.date).format('YYYYMM'),
    nextDate: getNextInvoiceDate(data)
  }

  return mode === 'edit'
    ? { ...data, ...common, lastModBy: user.email }
    : { ...data, ...common, company_id: user.company_id, createdBy: user.email }
}

function Outcome({ modeProps = null, checkForm, setCheckForm, value, setReturnValue, onValidSubmit }) {
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [editOutcome, setEditOutcome] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const [displayMode, setDisplayMode] = useState(modeProps)
  const path = location.pathname
  const outcomeId = location.state?.id

  const mode = useMemo(() => {
    if (displayMode === 'dialog') return 'add'
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path, displayMode])

  const outcomeQuery =
    mode === 'edit' || mode === 'view' ? { key: 'outcome', fn: getObject, args: ['outcomes', outcomeId] } : null
  const { data: fetched, loading: loadingEntities } = useFetchEntities([outcomeQuery].filter(Boolean))

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'outcomes',
    mode,
    id: outcomeId,
    user,
    args: [editOutcome],
    addFn: addObject,
    editFn: editObject,
    buildPayload,
    onSuccess: async (res) => {
      const { id, creditNote_id: creditNoteId, lastModDateTime } = res.data

      if (!lastModDateTime) {
        try {
          const resE = await editObject('creditNotes', creditNoteId, {
            status: 80,
            lastModBy: user.email
          })

          if (displayMode === 'dialog') {
            setReturnValue?.(resE.data)
            onValidSubmit?.()
          }
        } catch {}
      }
    },

    onError: (err) => {
      toast.error('Erreur lors de la sauvegarde', { autoClose: 2000 })
    },
    onSuccessRedirect: (id) => {
      if (displayMode !== 'dialog') {
        history(location.state?.history || ROUTES.VIEW, { state: { id: id } })
      }
    }
  })

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        if (path.includes('/add')) return 'Nouveau Décaissement'

        const invoiceNumber = editOutcome?.invoice_number
        const creditNoteNumber = editOutcome?.creditNote?.creditNote_number
        return invoiceNumber
          ? `Décaissement Facture ${invoiceNumber}`
          : creditNoteNumber
          ? `Décaissement Avoir ${creditNoteNumber}`
          : 'Décaissement'
      },
      objectType: 'outcomes',
      actions: { edit: true, files: true, back: true, audits: true, list: true }
    }),
    [userRight, editOutcome]
  )

  const fields = useMemo(() => getOutcomeFields(displayMode, user, location), [user, displayMode, location])

  const formOptions = useMemo(
    () => ({
      fields,
      data: editOutcome,
      location: location
    }),
    [fields, editOutcome, location]
  )

  useEffect(() => {
    let isMounted = true

    const fetchCreditNote = async () => {
      if (!editOutcome?.creditNote_id) return

      try {
        const res = await getObject('creditNotes', editOutcome.creditNote_id)
        const creditNote = res.data
        if (!isMounted || !creditNote) return

        setEditOutcome((prev) => {
          if (creditNote.customer_id) {
            return { ...prev, customer_id: creditNote.customer_id, supplier_id: null }
          } else if (creditNote.supplier_id) {
            return { ...prev, supplier_id: creditNote.supplier_id, customer_id: null }
          }
          return prev
        })
      } catch (err) {}
    }

    fetchCreditNote()

    return () => {
      isMounted = false
    }
  }, [editOutcome?.creditNote_id])

  useEffect(() => {
    if (displayMode === 'dialog' && value) {
      setEditOutcome((prev) => ({
        ...prev,
        creditNote_id: value.id,
        type: value.customer_id !== undefined ? 1 : 2,
        customer_id: value.customer_id,
        amount: value.total_net_amount
      }))
    }
  }, [displayMode, value])

  useEffect(() => {
    if (!loadingEntities && fetched?.outcome) {
      setEditOutcome(fetched.outcome)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (isSubmit) {
      saveEntity({ data: editOutcome })
      setIsSubmit(false)
    }
  }, [isSubmit, editOutcome, saveEntity])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbar} object={editOutcome} />
      <Form
        mode={mode}
        options={formOptions}
        modeProps={modeProps}
        setDataParent={setEditOutcome}
        setIsSubmitParent={setIsSubmit}
        checkForm={checkForm}
        setCheckForm={setCheckForm}
        loading={loading}
      />
    </Box>
  )
}

export default Outcome
